import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { InfoIcon } from 'lucide-react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface PartnerUser {
  id: number;
  name: string;
  fatherName: string;
  idNumber: string;
  email: string;
  phone: string;
  username: string;
  partner: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface PartnerUsersTableProps {
  users: PartnerUser[];
  onEditUser: (user: PartnerUser) => void;
  searchQuery: string;
  partnerFilter: string | null;
  onStatusChange?: (user: PartnerUser, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: PartnerUser) => void;
  onViewResetHistory?: (user: PartnerUser) => void;
  onResendEmail?: (user: PartnerUser) => void;
}
const PARTNERS = ['Al Ansari Exchange', 'Al Fardan Exchange', 'UAE Exchange', 'Lulu Exchange', 'Xpress Money'];
const PartnerUsersTable: React.FC<PartnerUsersTableProps> = ({
  users,
  onEditUser,
  searchQuery,
  partnerFilter,
  onStatusChange,
  onResetPassword,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    partners: partnerFilter ? [partnerFilter] : [] as string[],
    status: 'all',
    userType: 'all',
    dateFrom: '',
    dateTo: '',
    search: searchQuery || '',
    name: '',
    fatherName: '',
    idNumber: '',
    email: '',
    phone: '',
    username: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Handle text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Apply filters to the data
  const filteredUsers = users.filter(user => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!user.name.toLowerCase().includes(query) && !user.email.toLowerCase().includes(query) && !user.idNumber.toLowerCase().includes(query) && !user.phone.toLowerCase().includes(query) && !user.username.toLowerCase().includes(query) && !user.partner.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific text filters
    if (filters.name && !user.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.fatherName && !user.fatherName.toLowerCase().includes(filters.fatherName.toLowerCase())) {
      return false;
    }
    if (filters.idNumber && !user.idNumber.toLowerCase().includes(filters.idNumber.toLowerCase())) {
      return false;
    }
    if (filters.email && !user.email.toLowerCase().includes(filters.email.toLowerCase())) {
      return false;
    }
    if (filters.phone && !user.phone.toLowerCase().includes(filters.phone.toLowerCase())) {
      return false;
    }
    if (filters.username && !user.username.toLowerCase().includes(filters.username.toLowerCase())) {
      return false;
    }
    // Partner filter
    if (filters.partners.length > 0 && !filters.partners.includes(user.partner)) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && user.status !== filters.status) {
      return false;
    }
    // User type filter from main filters
    if (filters.userType !== 'all' && user.userType !== filters.userType) {
      return false;
    }
    // User type filter from column filter
    if (columnFilters.userType && columnFilters.userType !== 'all' && user.userType !== columnFilters.userType) {
      return false;
    }
    // Status filter from column filter
    if (columnFilters.status && columnFilters.status !== 'all' && user.status !== columnFilters.status) {
      return false;
    }
    // Partner filter from column filter
    if (columnFilters.partner && columnFilters.partner !== 'all' && user.partner !== columnFilters.partner) {
      return false;
    }
    // Date filters
    if (filters.dateFrom) {
      const userDate = new Date(user.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) {
        return false;
      }
    }
    if (filters.dateTo) {
      const userDate = new Date(user.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) {
        return false;
      }
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      partners: [],
      status: 'all',
      userType: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      name: '',
      fatherName: '',
      idNumber: '',
      email: '',
      phone: '',
      username: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Define filter chips
  const filterChips: FilterChip[] = [...(filters.name ? [{
    id: 'name',
    label: 'Name',
    value: filters.name,
    onDelete: () => handleTextFilterChange('name', '')
  }] : []), ...(filters.fatherName ? [{
    id: 'fatherName',
    label: 'Father Name',
    value: filters.fatherName,
    onDelete: () => handleTextFilterChange('fatherName', '')
  }] : []), ...(filters.idNumber ? [{
    id: 'idNumber',
    label: 'ID Number',
    value: filters.idNumber,
    onDelete: () => handleTextFilterChange('idNumber', '')
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => handleTextFilterChange('email', '')
  }] : []), ...(filters.phone ? [{
    id: 'phone',
    label: 'Phone',
    value: filters.phone,
    onDelete: () => handleTextFilterChange('phone', '')
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => handleTextFilterChange('username', '')
  }] : []), ...filters.partners.map(partner => ({
    id: `partner-${partner}`,
    label: 'Partner',
    value: partner,
    onDelete: () => setFilters({
      ...filters,
      partners: filters.partners.filter(p => p !== partner)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...(filters.userType !== 'all' ? [{
    id: 'userType',
    label: 'User Type',
    value: filters.userType === 'maker' ? 'Maker' : 'Checker',
    onDelete: () => setFilters({
      ...filters,
      userType: 'all'
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : []), ...Object.entries(columnFilters).filter(([key, value]) => value !== 'all' && value !== '').map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: columns.find(col => col.id === key)?.label || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, key === 'status' || key === 'userType' || key === 'partner' ? 'all' : '')
  }))];
  // Define columns
  const columns: Column<PartnerUser>[] = [{
    id: 'name',
    label: 'Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.name} onChange={e => handleTextFilterChange('name', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1744" data-mp="28uijsmrho7at">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1745" data-mp="2dr8x52bwsosj" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1743" data-mp="2abjar4ae9hiv" />
  }, {
    id: 'fatherName',
    label: 'Father Name',
    renderFilter: () => <TextField size="small" placeholder="Search father name" value={filters.fatherName} onChange={e => handleTextFilterChange('fatherName', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1747" data-mp="3ajcgzzsafolf">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1748" data-mp="3fh4cf3s55sud" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1746" data-mp="2rrs1nzh61cpy" />
  }, {
    id: 'idNumber',
    label: 'ID Number',
    renderFilter: () => <TextField size="small" placeholder="Search ID" value={filters.idNumber} onChange={e => handleTextFilterChange('idNumber', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1750" data-mp="2ftpj7wx7iuk">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1751" data-mp="2eiohfuv0p75" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1749" data-mp="3kob1ad2l2q88" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search email" value={filters.email} onChange={e => handleTextFilterChange('email', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1753" data-mp="2fkyr7rvo9y73">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1754" data-mp="21n0qvqe6s2al" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1752" data-mp="q54kg8rlaj0s" />
  }, {
    id: 'phone',
    label: 'Phone',
    renderFilter: () => <TextField size="small" placeholder="Search phone" value={filters.phone} onChange={e => handleTextFilterChange('phone', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1756" data-mp="1agjrf236d9rm">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1757" data-mp="1adp7rcrzjsz9" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1755" data-mp="1wnxjhzaqhjzg" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search username" value={filters.username} onChange={e => handleTextFilterChange('username', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1759" data-mp="8qep9lx72eob">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1760" data-mp="8rpy405hycma" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1758" data-mp="1y3jinosqv7gv" />
  }, {
    id: 'partner',
    label: 'Partner',
    renderCell: row => <Chip label={row.partner} size="small" sx={{
      backgroundColor: '#E0F7FF',
      color: '#0EA5E9',
      height: 24
    }} data-id="element-1761" data-mp="k6t2y72redzo" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1762" data-mp="3nkdo5iwqwbbg">
          <Select value={columnFilters.partner || 'all'} onChange={e => handleColumnFilterChange('partner', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1763" data-mp="3smcrisshj88e">
            <MenuItem value="all" data-id="element-1764" data-mp="34u60881m9rfu">All Partners</MenuItem>
            {PARTNERS.map(partner => <MenuItem key={partner} value={partner} data-id="element-1765" data-mp="39sqora7iamog">
                {partner}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'userType',
    label: 'User Type',
    renderCell: row => <Chip label={row.userType === 'maker' ? 'Maker' : 'Checker'} size="small" sx={{
      backgroundColor: row.userType === 'maker' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.userType === 'maker' ? '#06B6D4' : '#8B5CF6',
      height: 24,
      fontWeight: 600
    }} data-id="element-1766" data-mp="1hxzgxqpgv5it" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1767" data-mp="u9oisvb5njy8">
          <Select value={columnFilters.userType || 'all'} onChange={e => handleColumnFilterChange('userType', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1768" data-mp="ubj6uihq3s6j">
            <MenuItem value="all" data-id="element-1769" data-mp="fmpzjnmgbvul">All Types</MenuItem>
            <MenuItem value="maker" data-id="element-1770" data-mp="foaqz4dgyyo8">Maker</MenuItem>
            <MenuItem value="checker" data-id="element-1771" data-mp="1nq704vtiwt5">Checker</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1772" data-mp="teo0hb60elfo" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1773" data-mp="1cc7aoi47ea3o">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1774" data-mp="1h3yv0awrnjld">
            <MenuItem value="all" data-id="element-1775" data-mp="3bkqgfwmnhaof">All Status</MenuItem>
            <MenuItem value="active" data-id="element-1776" data-mp="3bjfapr9n9suo">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1777" data-mp="36kub9u27vd7a">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Define row actions
  const rowActions: RowAction<PartnerUser>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: user => console.log('View user', user)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditUser
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: user => onResendEmail?.(user),
    condition: user => shouldShowResendEmail(user)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: user => onStatusChange?.(user, 'inactive'),
    condition: user => user.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: user => onStatusChange?.(user, 'active'),
    condition: user => user.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: user => onResetPassword?.(user)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: user => onViewResetHistory?.(user)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: user => console.log('View audit trail for user', user)
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
  };
  return <DataTable title="Partner Users" data={filteredUsers} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['name', 'email', 'phone', 'username', 'partner', 'userType', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search users by name, email, phone..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No users found" data-id="element-1778" data-mp="2uacsh4sdrt52" />;
};
export default PartnerUsersTable;